import { z } from 'zod';

import type {
  V1Automation,
  V1AutomationInputSpec,
  V1CodeManifest,
  V1CodeInput,
  V1CodeOutput,
  V1Step,
} from '@/generated/hooks/grimoire/models';
import { convertKeysToSnakeCase } from '@/shared/utils/convert-keys';
import { decodeArtifactData } from '@/modules/rain/utils/artifact';
import { captureException } from '@/modules/sentry';

const positionSchema = z.looseObject({
  line: z.number().optional(),
  column: z.number().optional(),
});

const locationSchema = z.looseObject({
  start_byte: z.union([z.string(), z.number()]).optional(),
  end_byte: z.union([z.string(), z.number()]).optional(),
  start_position: positionSchema.optional(),
  end_position: positionSchema.optional(),
});

const valueTypeSchema = z.looseObject({
  scalar_type: z.string().optional(),
});

const codeIoSchema = z.object({
  name: z.string().optional(),
  type: valueTypeSchema.optional(),
});

const functionNodeSchema: z.ZodType = z.lazy(() =>
  z.looseObject({
    name: z.string().optional(),
    docstring: z.string().optional(),
    location: locationSchema.optional(),
    calls: z.array(functionNodeSchema).optional(),
  }),
);

const auditSchema = z.object({
  external_calls: z.array(z.unknown()).optional(),
  literals: z.array(z.unknown()).optional(),
});

const stepSchema = z.looseObject({
  number: z.number().optional(),
  call_location: locationSchema.optional(),
  entry_function: functionNodeSchema.optional(),
  audit: auditSchema.optional(),
  title: z.string().optional(),
  overview: z.string().optional(),
  rules: z.array(z.string()).optional(),
  returns: z.string().optional(),
  phase: z.string().optional(),
});

export const codeManifestSchema = z.looseObject({
  code_hash: z.string().optional(),
  main: z.object({ location: locationSchema.optional() }).optional(),
  inputs: z.array(codeIoSchema).optional(),
  outputs: z.array(codeIoSchema).optional(),
  steps: z.array(stepSchema).optional(),
  title: z.string().optional(),
  overview: z.string().optional(),
});

export const CODE_MANIFEST_ARTIFACT_KEY = 'code_manifest.json';

export function parseCodeManifest(json: string): V1CodeManifest {
  const parsed = convertKeysToSnakeCase(JSON.parse(json));
  const result = codeManifestSchema.safeParse(parsed);
  if (!result.success) {
    captureException(result.error, {
      tags: { source: 'code-manifest' },
      extra: { issues: result.error.issues },
    });
    throw result.error;
  }
  return result.data as V1CodeManifest;
}

export function decodeCodeManifest(
  base64?: string,
): V1CodeManifest | undefined {
  if (!base64) return undefined;
  const json = decodeArtifactData(base64);
  if (!json) return undefined;
  return parseCodeManifest(json);
}

export function decodeCodeManifestFromAutomation(
  automation: V1Automation | undefined,
): V1CodeManifest | undefined {
  if (automation?.code_manifest) {
    return automation.code_manifest;
  }
  return decodeCodeManifest(
    automation?.artifacts?.[CODE_MANIFEST_ARTIFACT_KEY],
  );
}

/**
 * True for a manifest that predates jarvis's `title`/`overview`/`phase`
 * step metadata — the `code_manifest.json` artifact shape the agent no
 * longer writes, but that still exists on automations saved before it
 * shipped. Steps view has nothing to render but "Untitled step" for these;
 * treat it the same as a missing manifest for the upgrade banner.
 */
export function isManifestStale(manifest: V1CodeManifest | undefined): boolean {
  if (!manifest?.steps?.length) return false;
  return manifest.steps.every(step => !step.title);
}

export function manifestInputsToInputSpecs(
  inputs: V1CodeInput[] | undefined,
): V1AutomationInputSpec[] {
  return (inputs ?? []).map(i => ({
    name: i.name ?? '',
    type: i.type as V1AutomationInputSpec['type'],
  }));
}

export function manifestOutputsToMarkedOutputs(
  outputs: V1CodeOutput[] | undefined,
): string[] {
  return (outputs ?? []).map(o => o.name ?? '');
}

/**
 * The outputs a given step produces, attributed from the automation-wide
 * `manifest.outputs` (the manifest carries no per-step typed outputs — every
 * `set_output` aggregates there). A step's `set_output(name, …)` records `name`
 * as a string literal in that step's `audit.literals`, so an automation output
 * whose name is a literal in THIS step's own function is one of its outputs. The
 * match is scoped by `containing_function` because `audit.literals` is collected
 * transitively — a shared helper's literal must not attribute an output to a
 * caller step (`containing_function == null` is accepted for manifests that omit
 * it).
 *
 * A step with no attributed output scores on RETURN, and we do NOT fall back to
 * the automation-wide outputs when nothing matches (e.g. every `set_output` name
 * is built dynamically, or the output is bound in `main` rather than inside any
 * step). An isolated step run only populates the RETURN channel — main's output
 * bindings are never executed — so attributing an output the step does not set
 * would score it against an always-empty OUTPUTS channel.
 *
 * The step count is deliberately not a signal: in the common AOP shape `main`
 * calls the one step and binds its result with `set_output`, so that output is
 * main's and the step still scores on RETURN.
 *
 * Limitations (inherent to a literal-based signal, both resolvable only by a
 * dedicated jarvis per-step-outputs manifest field — the intended next step, not
 * indefinite future work):
 *  - Under-attribution: when other steps attribute outputs but this one does
 *    not, it is treated as a RETURN step. Correct for a genuine return-only step,
 *    but a step that DOES set an output the heuristic can't see — a
 *    dynamically-built `set_output` name, or `set_output` called inside a helper
 *    (whose literal carries the helper's `containing_function`, not this step's)
 *    — is indistinguishable here and gets mis-scored on the empty RETURN channel.
 *  - Mis-attribution: a step that merely MENTIONS another output's name as a
 *    string literal in its own body would be wrongly attributed that output.
 */
export function getStepOwnOutputs(
  step: V1Step | undefined,
  allOutputs: V1CodeOutput[],
): V1CodeOutput[] {
  const fn = step?.entry_function?.name;
  const names = new Set(
    (step?.audit?.literals ?? [])
      .filter(
        l => l.containing_function === fn || l.containing_function == null,
      )
      .map(l => l.value)
      .filter((v): v is string => !!v),
  );
  return allOutputs.filter(o => o.name != null && names.has(o.name));
}

export type ResolvedAutomation = V1Automation & {
  manifest?: V1CodeManifest;
  manifest_outputs?: V1CodeOutput[];
};

export function resolveAutomation(
  automation: V1Automation,
  manifest?: V1CodeManifest,
): ResolvedAutomation {
  const effectiveManifest =
    manifest ?? decodeCodeManifestFromAutomation(automation);
  if (!effectiveManifest) {
    return {
      ...automation,
      manifest: undefined,
      manifest_outputs: undefined,
    };
  }
  return {
    ...automation,
    input_specs: manifestInputsToInputSpecs(effectiveManifest.inputs),
    marked_outputs: manifestOutputsToMarkedOutputs(effectiveManifest.outputs),
    manifest: effectiveManifest,
    manifest_outputs: effectiveManifest.outputs,
  };
}
